import { useField } from 'formik'
import { useTheme } from 'styled-components'

import { Checkbox } from '@/@bothrs-ui/web/components/forms/checkbox/Checkbox'
import { FlexColumn, FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { StAside, StSelectListItemContainer } from './SelectListItem.styled'

import type { InputHTMLAttributes } from 'react'
import type { PartiallyRequired } from '@/utils/typescript'

type SelectListItemProps = {
  label?: string
  description?: string
  aside?: string
  type: 'radio' | 'checkbox'
  checked?: boolean
  onChange?: (event?: React.ChangeEvent<HTMLInputElement>) => void
} & PartiallyRequired<Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'onChange'>, 'name' | 'id'>

const SelectListItem = ({
  label,
  description,
  aside,
  checked: controlledChecked,
  onChange: controlledOnChange,
  disabled,
  ...props
}: SelectListItemProps) => {
  const theme = useTheme()
  const [field] = useField({
    ...props,
    value: props.value?.toString(), // Only string values are supported
  })

  // Support controlled mode when checked/onChange are provided
  const isChecked = controlledChecked ?? field.checked

  // Ensure value is always a string to prevent controlled/uncontrolled warning
  const inputValue = field.value ?? props.value?.toString() ?? ''

  return (
    <StSelectListItemContainer htmlFor={props.id} $disabled={disabled}>
      <input
        {...field}
        id={props.id}
        name={props.name}
        value={inputValue}
        type={props.type}
        checked={isChecked}
        disabled={disabled}
        onChange={controlledOnChange ?? field.onChange}
      />

      <FlexColumn>
        <FlexRow justifyContent='space-between'>
          {props.type === 'checkbox' ? (
            <Checkbox
              {...field}
              id={props.id}
              name={props.name}
              value={inputValue}
              type={props.type}
              checked={isChecked}
              disabled={disabled}
              onChange={controlledOnChange ?? field.onChange}
            />
          ) : null}
          <span style={{ flex: 1 }}>{label}</span>
          {aside ? <StAside>{aside}</StAside> : null}
        </FlexRow>
        {description ? (
          <>
            <Padding py={theme.UI.Spacing.Space2} />
            <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
              {description}
            </Span>
          </>
        ) : null}
      </FlexColumn>
    </StSelectListItemContainer>
  )
}

export { SelectListItem }
